import { z } from "zod";
import type { Operation } from "../types";

/**
 * Pulse Telemetry — the Dynamic Pulse wire contract (docs/pulse/DYNAMIC-PULSE-INTEGRATION-PRD.md
 * §4 P2). `GET /api/pulse/telemetry/[scope]` returns ONE `SectionTelemetry` for
 * the scope's section (the learned-baseline battery: elected anchor + surfaced
 * findings + quiet series), or `section: null` when no stream serves the scope.
 *
 * ONE fetch serves the rail, the peek, AND the insight modal: every series
 * carries its full 28-day `points[]` plus baseline/spread/z/mass/coupling, so
 * the modal renders from this single payload with no second request. Mirrors the
 * `pulse.aggregate` contract family: a `scope` path param, `createRoute`-wrapped,
 * nested shapes `.passthrough()`d so an additive engine field never trips the
 * client parser.
 *
 * `scope` maps to a section via `sectionForScope` (streams.ts): `"work"`/
 * `"work:queue"` → work, `"agents"`/`"agents:runs"` → agents, `"customers:*"` →
 * customers, `"operations:*"` → operations, anything else → null.
 *
 * P4 (§4 P4) adds the insight modal footer's third write seam — `watch`
 * (`POST /api/pulse/telemetry/watch`) pins a series into `surfaced` regardless
 * of z via a per-workspace `settings` row `pulse.watch.<seriesId>` (the same
 * seam `pulse.setTarget` uses for tunable targets). "Propose a fix" and "Open
 * the filtered view" reuse EXISTING seams (`play.propose`, `series.viewHref`)
 * rather than adding more operations here.
 *
 * P5 (§4 P5) adds `anchor` (`POST /api/pulse/telemetry/anchor`) — accept/reject
 * a drift or Tier-2 anchor proposal. `accept` writes `pulse.anchor.<section>` =
 * seriesId (persists across re-elections) and logs an `activities` row;
 * `reject` stamps `pulse.anchor.dismissed.<section>` so the SAME challenger
 * doesn't re-surface for 14 days. NEVER auto-applied — this route (and the
 * `pulse-anchor` Play answer branch, which calls the same underlying
 * accept/reject functions directly) is the only writer.
 *
 * P6 (§4 P6) adds `nav` (`GET /api/pulse/telemetry/nav`) — the left ModeRail's
 * read model. ONE call serves BOTH the mode row's decoration AND its expanded
 * sub-nav dots: a light per-section summary (elected anchor + a bounded
 * recent-days trend, worst surfaced-finding tone, quiet count) PLUS the surfaced
 * findings folded by the sub-nav area that owns them (`areas`), instead of the
 * full battery `section` returns — the rail draws a micro-sparkline, a severity
 * tone, and a per-sub-view health dot, not the insight modal. Entries exist only
 * for sections a stream serves (work/agents/customers/operations); a mode with
 * no dynamic section (product, personal) is simply absent.
 */

const UNITS = ["per-day", "pct", "usd", "hours", "minutes"] as const;
const DIRECTIONS = ["up-good", "down-good", "attention"] as const;
const FINDING_KINDS = ["regression", "improvement", "shift"] as const;

/** One learned series — its full 28-day shape + robust stats. */
export const telemetrySeries = z
  .object({
    id: z.string(),
    section: z.string(),
    label: z.string(),
    unit: z.enum(UNITS),
    direction: z.enum(DIRECTIONS),
    /** Daily values aligned to `days` (null = no observation that day). */
    points: z.array(z.number().nullable()),
    baseline: z.number(),
    spread: z.number(),
    /** `null` = the recent window carries NO reading (a ratio/level series whose
     *  recent denominator is 0). Renders as an em-dash, never as a number: the
     *  engine used to substitute the baseline median here and every consumer
     *  drew it as the CURRENT rate. */
    recent: z.number().nullable(),
    z: z.number(),
    mass: z.number(),
    basis: z.number(),
    sufficient: z.boolean(),
    sliceDim: z.string().optional(),
    parent: z.string().optional(),
    folded: z.boolean().optional(),
    coupling: z.number().optional(),
    /** P4: pinned via the per-workspace `pulse.watch.<id>` setting — always
     *  surfaced regardless of z when true. */
    watched: z.boolean().optional(),
    /** P4: the "open the filtered view" target (`StreamDef.viewHref`) — a route
     *  showing the rows behind the number, or null when none applies. */
    viewHref: z.string().nullable().optional(),
  })
  .passthrough();
export type TelemetrySeriesDTO = z.infer<typeof telemetrySeries>;

/** A slice's contribution to a surfaced parent's delta (anchor/finding drill). */
export const telemetryCause = z
  .object({
    label: z.string(),
    share: z.number(),
    baselinePerDay: z.number(),
    /** `null` when the slice's recent window carries no reading. */
    recentPerDay: z.number().nullable(),
  })
  .passthrough();
export type TelemetryCauseDTO = z.infer<typeof telemetryCause>;

/** One candidate in the anchor election (Tier-1 scoring). */
export const telemetryAnchorCandidate = z
  .object({
    id: z.string(),
    label: z.string(),
    unit: z.enum(UNITS),
    score: z.number(),
    reasons: z.array(z.string()),
    /** `null` when the recent window carries no reading. */
    recent: z.number().nullable(),
    baseline: z.number(),
    /** `null` when `recent` is null — no pace to report. */
    ratio: z.number().nullable(),
  })
  .passthrough();
export type TelemetryAnchorCandidateDTO = z.infer<typeof telemetryAnchorCandidate>;

/** A confirm-gated proposal to re-anchor the section (never auto-applied). */
export const telemetryAnchorProposal = z
  .object({
    fromId: z.string(),
    fromLabel: z.string(),
    toId: z.string(),
    toLabel: z.string(),
    reason: z.string(),
    simulated: z.boolean(),
  })
  .passthrough();
export type TelemetryAnchorProposalDTO = z.infer<typeof telemetryAnchorProposal>;

/** The section's elected anchor (velocity) + its own decomposition. */
export const telemetryAnchor = z
  .object({
    id: z.string(),
    label: z.string(),
    unit: z.enum(UNITS),
    /** `null` when the recent window carries no reading. */
    recent: z.number().nullable(),
    baseline: z.number(),
    /** `null` when `recent` is null — no pace to report. */
    ratio: z.number().nullable(),
    points: z.array(z.number().nullable()),
    causes: z.array(telemetryCause),
  })
  .passthrough();
export type TelemetryAnchorDTO = z.infer<typeof telemetryAnchor>;

/** A surfaced deviation (a "finding"): the deviant series + its cause decomposition. */
export const telemetryFinding = z
  .object({
    kind: z.enum(FINDING_KINDS),
    severity: z.enum(["high", "medium"]),
    interest: z.number(),
    series: telemetrySeries,
    causes: z.array(telemetryCause),
  })
  .passthrough();
export type TelemetryFindingDTO = z.infer<typeof telemetryFinding>;

/** ONE section's full telemetry — anchor (+candidates +proposal), surfaced, quiet. */
export const sectionTelemetry = z
  .object({
    section: z.string(),
    label: z.string(),
    measured: z.number(),
    anchor: telemetryAnchor.optional(),
    anchorCandidates: z.array(telemetryAnchorCandidate),
    anchorProposal: telemetryAnchorProposal.optional(),
    surfaced: z.array(telemetryFinding),
    quiet: z.array(telemetrySeries),
  })
  .passthrough();
export type SectionTelemetryDTO = z.infer<typeof sectionTelemetry>;

const telemetryParams = z.object({ scope: z.string().min(1) });
export type PulseTelemetryParams = z.infer<typeof telemetryParams>;

const telemetryResponse = z.object({
  /** The trailing window's ISO days (oldest first) `points[]` align to. */
  days: z.array(z.string()),
  /** The recent-window length used for the "this week vs baseline" split. */
  recentDays: z.number(),
  /** The scope's section telemetry, or null when no stream serves the scope. */
  section: sectionTelemetry.nullable(),
});
export type PulseTelemetryResponse = z.infer<typeof telemetryResponse>;

const sectionOp: Operation<PulseTelemetryParams, void, void, PulseTelemetryResponse> = {
  method: "GET",
  path: (p) => `/api/pulse/telemetry/${encodeURIComponent(p.scope)}`,
  params: telemetryParams,
  response: telemetryResponse,
};

// ── pulse.telemetry.nav — the ModeRail's one-call read model (§4 P6) ─────────
//
// A finding-kind tone, mirroring the dynamic gutter rail's `KIND_FILL`
// vocabulary (packages/ui/src/blocks/pulse/pulse-gutter-rail.tsx — a local,
// non-exported const there) so a nav row's color reads consistently with the
// rail's own finding rows.
const NAV_FINDING_TONES = ["regression-high", "regression-medium", "improvement", "shift"] as const;

export const telemetryNavAnchor = z
  .object({
    id: z.string(),
    label: z.string(),
    /** `null` when the anchor's recent window carries no reading — the pace
     *  meter reads "no data" rather than drawing a stale fill. */
    ratio: z.number().nullable(),
    /** Bounded trailing window (RECENT_DAYS values) for a micro-sparkline —
     *  gap days coerced to 0, never null (one bar per day, always drawable). */
    recentDaily: z.array(z.number()),
  })
  .passthrough();
export type TelemetryNavAnchorDTO = z.infer<typeof telemetryNavAnchor>;

export const telemetryNavEntry = z
  .object({
    section: z.string(),
    /** Null when the section has no elected anchor yet (no data) — the client
     *  renders nothing for this row (the same "un-instrumented ⇒ silent" calm
     *  default the classic sparkline already follows). */
    anchor: telemetryNavAnchor.nullable(),
    surfaced: z
      .object({
        count: z.number(),
        /** The most alarming tone among this section's surfaced findings —
         *  NOT necessarily the top (highest-interest) finding's own tone. Null
         *  when nothing surfaced (a quiet section). */
        worstKind: z.enum(NAV_FINDING_TONES).nullable(),
      })
      .passthrough(),
    /** The surfaced findings folded by the sub-nav AREA that owns them (only
     *  areas with ≥1 surfaced finding, worst-tone-first) — the read model for
     *  the ModeRail's per-sub-view health dots. A finding whose series maps to
     *  no sub-nav row is absent here but still counts in `surfaced` above. */
    areas: z.array(
      z
        .object({
          area: z.string(),
          count: z.number(),
          worstKind: z.enum(NAV_FINDING_TONES),
        })
        .passthrough(),
    ),
    quietCount: z.number(),
  })
  .passthrough();
export type TelemetryNavEntryDTO = z.infer<typeof telemetryNavEntry>;

const navResponse = z.object({
  /** One entry per section a stream serves — never one per mode. A mode with
   *  no dynamic section (product, personal) is simply absent. */
  entries: z.array(telemetryNavEntry),
});
export type PulseTelemetryNavResponse = z.infer<typeof navResponse>;

const navOp: Operation<void, void, void, PulseTelemetryNavResponse> = {
  method: "GET",
  path: () => "/api/pulse/telemetry/nav",
  response: navResponse,
};

// ── pulse.telemetry.watch — pin a series into `surfaced` (§4 P4) ─────────────
//
// `POST /api/pulse/telemetry/watch` writes/clears a per-workspace `settings`
// row `pulse.watch.<seriesId>` (additive JSON, NO migration — the exact seam
// `pulse.setTarget` uses). The engine reads the whole watch set once per
// compute (never per series) and pins a match into `surfaced` regardless of z.

const watchBody = z.object({
  seriesId: z.string().min(1),
  watched: z.boolean(),
});
export type PulseWatchBody = z.infer<typeof watchBody>;

const watchResponse = z.object({ ok: z.boolean(), watched: z.boolean() });
export type PulseWatchResponse = z.infer<typeof watchResponse>;

const watchOp: Operation<void, void, PulseWatchBody, PulseWatchResponse> = {
  method: "POST",
  path: () => "/api/pulse/telemetry/watch",
  body: watchBody,
  response: watchResponse,
};

// ── pulse.telemetry.anchor — accept/reject an anchor proposal (§4 P5) ────────

const anchorActions = ["accept", "reject"] as const;

const anchorBody = z.object({
  section: z.string().min(1),
  action: z.enum(anchorActions),
  /** The challenger id (`AnchorProposal.toId`) — the series that either
   *  becomes the anchor (`accept`) or is suppressed for 14 days (`reject`). */
  seriesId: z.string().min(1),
});
export type PulseAnchorBody = z.infer<typeof anchorBody>;

const anchorResponse = z.object({
  ok: z.boolean(),
  section: z.string(),
  seriesId: z.string(),
  action: z.enum(anchorActions),
});
export type PulseAnchorResponse = z.infer<typeof anchorResponse>;

const anchorOp: Operation<void, void, PulseAnchorBody, PulseAnchorResponse> = {
  method: "POST",
  path: () => "/api/pulse/telemetry/anchor",
  body: anchorBody,
  response: anchorResponse,
};

export const pulseTelemetry = {
  section: sectionOp,
  nav: navOp,
  watch: watchOp,
  anchor: anchorOp,
};
